import * as fuzzball from 'fuzzball';

export type ConfidenceLevel = 'high' | 'medium' | 'low' | 'none';

export interface MatchResult {
  score: number;
  confidence: ConfidenceLevel;
  matchingFields: string[];
  details: Record<string, number>;
}

// Thresholds for confidence levels
const THRESHOLDS = {
  high: 90,
  medium: 70,
  low: 50,
};

/**
 * Get confidence level from a score
 */
export function getConfidenceLevel(score: number): ConfidenceLevel {
  if (score >= THRESHOLDS.high) return 'high';
  if (score >= THRESHOLDS.medium) return 'medium';
  if (score >= THRESHOLDS.low) return 'low';
  return 'none';
}

/**
 * Get color class for confidence level
 */
export function getConfidenceColor(confidence: ConfidenceLevel): string {
  switch (confidence) {
    case 'high':
      return 'bg-destructive text-destructive-foreground';
    case 'medium':
      return 'bg-warning text-warning-foreground';
    case 'low':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-secondary text-secondary-foreground';
  }
}

/**
 * Normalize a string for comparison
 */
function normalize(str: string | null | undefined): string {
  if (!str) return '';
  return str.toLowerCase().trim().replace(/\s+/g, ' ');
}

/**
 * Normalize phone number for comparison
 */
function normalizePhone(phone: string | null | undefined): string {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
}

/**
 * Calculate name similarity using multiple algorithms
 */
export function calculateNameSimilarity(
  name1: string | null | undefined,
  name2: string | null | undefined
): number {
  const n1 = normalize(name1);
  const n2 = normalize(name2);
  
  if (!n1 || !n2) return 0;
  if (n1 === n2) return 100;

  // Use multiple fuzzball algorithms and take the best
  const ratioScore = fuzzball.ratio(n1, n2);
  const partialRatioScore = fuzzball.partial_ratio(n1, n2);
  const tokenSortScore = fuzzball.token_sort_ratio(n1, n2);
  const tokenSetScore = fuzzball.token_set_ratio(n1, n2);

  // Weighted average favoring token-based matching for names
  return Math.round(
    ratioScore * 0.2 +
    partialRatioScore * 0.2 +
    tokenSortScore * 0.3 +
    tokenSetScore * 0.3
  );
}

/**
 * Calculate email similarity
 */
export function calculateEmailSimilarity(
  email1: string | null | undefined,
  email2: string | null | undefined
): number {
  const e1 = normalize(email1);
  const e2 = normalize(email2);
  
  if (!e1 || !e2) return 0;
  if (e1 === e2) return 100;

  // Split into local and domain parts
  const [local1, domain1] = e1.split('@');
  const [local2, domain2] = e2.split('@');

  if (!domain1 || !domain2) return 0;

  // Same domain is a strong signal
  const domainMatch = domain1 === domain2;
  const localSimilarity = fuzzball.ratio(local1 || '', local2 || '');

  if (domainMatch) {
    // If same domain, local part similarity is weighted heavily
    return Math.round(50 + localSimilarity * 0.5);
  }

  // Different domains - overall email similarity
  return Math.round(fuzzball.ratio(e1, e2) * 0.7);
}

/**
 * Calculate phone similarity
 */
export function calculatePhoneSimilarity(
  phone1: string | null | undefined,
  phone2: string | null | undefined
): number {
  const p1 = normalizePhone(phone1);
  const p2 = normalizePhone(phone2);
  
  if (!p1 || !p2) return 0;
  if (p1 === p2) return 100;

  // Check if one contains the other (with/without country code)
  if (p1.endsWith(p2) || p2.endsWith(p1)) {
    const longerLen = Math.max(p1.length, p2.length);
    const shorterLen = Math.min(p1.length, p2.length);
    // If the shorter is at least 10 digits (US number), it's a strong match
    if (shorterLen >= 10) return 95;
    return Math.round((shorterLen / longerLen) * 100);
  }

  // Use Levenshtein distance for partial matches
  return fuzzball.ratio(p1, p2);
}

/**
 * Calculate company name similarity with special handling for business suffixes
 */
export function calculateCompanySimilarity(
  company1: string | null | undefined,
  company2: string | null | undefined
): number {
  if (!company1 || !company2) return 0;

  // Remove common business suffixes for comparison
  const suffixes = /\b(inc\.?|llc\.?|ltd\.?|corp\.?|corporation|company|co\.?|plc\.?|limited|incorporated|pvt\.?|private)\b/gi;
  
  const c1 = normalize(company1).replace(suffixes, '').trim();
  const c2 = normalize(company2).replace(suffixes, '').trim();
  
  if (!c1 || !c2) return 0;
  if (c1 === c2) return 100;

  // Check for acronym matches
  const acronym1 = c1.split(' ').map(w => w[0]).join('');
  const acronym2 = c2.split(' ').map(w => w[0]).join('');
  
  if (acronym1.length > 1 && (acronym1 === c2 || c1 === acronym2)) {
    return 85; // Acronym match
  }

  // Token-based matching for company names
  const tokenSetScore = fuzzball.token_set_ratio(c1, c2);
  const tokenSortScore = fuzzball.token_sort_ratio(c1, c2);
  const ratioScore = fuzzball.ratio(c1, c2);

  return Math.round(
    tokenSetScore * 0.4 +
    tokenSortScore * 0.4 +
    ratioScore * 0.2
  );
}

/**
 * Calculate overall candidate match score
 */
export function calculateCandidateMatch(
  candidate1: {
    first_name?: string | null;
    last_name?: string | null;
    email_primary?: string | null;
    phone_primary?: string | null;
  },
  candidate2: {
    first_name?: string | null;
    last_name?: string | null;
    email_primary?: string | null;
    phone_primary?: string | null;
  }
): MatchResult {
  const details: Record<string, number> = {};
  const matchingFields: string[] = [];

  // Calculate individual scores
  const fullName1 = `${candidate1.first_name || ''} ${candidate1.last_name || ''}`.trim();
  const fullName2 = `${candidate2.first_name || ''} ${candidate2.last_name || ''}`.trim();
  
  const nameScore = calculateNameSimilarity(fullName1, fullName2);
  details.name = nameScore;
  if (nameScore >= THRESHOLDS.medium) matchingFields.push('name');

  const emailScore = calculateEmailSimilarity(candidate1.email_primary, candidate2.email_primary);
  details.email = emailScore;
  if (emailScore >= THRESHOLDS.high) matchingFields.push('email');

  const phoneScore = calculatePhoneSimilarity(candidate1.phone_primary, candidate2.phone_primary);
  details.phone = phoneScore;
  if (phoneScore >= THRESHOLDS.high) matchingFields.push('phone');

  // Weighted final score - email and phone are stronger signals
  let score = 0;
  let weights = 0;

  if (details.email > 0) {
    score += details.email * 0.4;
    weights += 0.4;
  }
  if (details.phone > 0) {
    score += details.phone * 0.35;
    weights += 0.35;
  }
  if (details.name > 0) {
    score += details.name * 0.25;
    weights += 0.25;
  }

  const finalScore = weights > 0 ? Math.round(score / weights) : 0;

  return {
    score: finalScore,
    confidence: getConfidenceLevel(finalScore),
    matchingFields,
    details,
  };
}

/**
 * Calculate overall company match score
 */
export function calculateCompanyMatch(
  company1: {
    name?: string | null;
    website_url?: string | null;
    location?: string | null;
  },
  company2: {
    name?: string | null;
    website_url?: string | null;
    location?: string | null;
  }
): MatchResult {
  const details: Record<string, number> = {};
  const matchingFields: string[] = [];

  // Name similarity
  const nameScore = calculateCompanySimilarity(company1.name, company2.name);
  details.name = nameScore;
  if (nameScore >= THRESHOLDS.medium) matchingFields.push('name');

  // Website similarity
  let websiteScore = 0;
  if (company1.website_url && company2.website_url) {
    const url1 = company1.website_url.toLowerCase().replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    const url2 = company2.website_url.toLowerCase().replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    websiteScore = url1 === url2 ? 100 : fuzzball.ratio(url1, url2);
    details.website = websiteScore;
    if (websiteScore >= THRESHOLDS.high) matchingFields.push('website');
  }

  // Location similarity
  let locationScore = 0;
  if (company1.location && company2.location) {
    locationScore = fuzzball.token_set_ratio(
      normalize(company1.location),
      normalize(company2.location)
    );
    details.location = locationScore;
    if (locationScore >= THRESHOLDS.medium) matchingFields.push('location');
  }

  // Weighted score - name and website are strongest signals
  let score = details.name * 0.5;
  if (details.website > 0) score += details.website * 0.35;
  else score += details.name * 0.35; // If no website, rely more on name
  
  if (details.location > 0) score += details.location * 0.15;

  const finalScore = Math.round(score);

  return {
    score: finalScore,
    confidence: getConfidenceLevel(finalScore),
    matchingFields,
    details,
  };
}

/**
 * Find potential duplicates in a list of candidates
 */
export function findCandidateDuplicates(
  candidates: Array<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email_primary?: string | null;
    phone_primary?: string | null;
  }>,
  minScore: number = THRESHOLDS.low
): Array<{
  primary: typeof candidates[0];
  duplicates: Array<{ candidate: typeof candidates[0]; match: MatchResult }>;
}> {
  const seen = new Set<string>();
  const groups: Array<{
    primary: typeof candidates[0];
    duplicates: Array<{ candidate: typeof candidates[0]; match: MatchResult }>;
  }> = [];

  for (let i = 0; i < candidates.length; i++) {
    const candidate = candidates[i];
    if (seen.has(candidate.id)) continue;

    const duplicates: Array<{ candidate: typeof candidates[0]; match: MatchResult }> = [];

    for (let j = i + 1; j < candidates.length; j++) {
      const other = candidates[j];
      if (seen.has(other.id)) continue;

      const match = calculateCandidateMatch(candidate, other);
      if (match.score >= minScore) {
        duplicates.push({ candidate: other, match });
        seen.add(other.id);
      }
    }

    if (duplicates.length > 0) {
      groups.push({
        primary: candidate,
        duplicates: duplicates.sort((a, b) => b.match.score - a.match.score),
      });
      seen.add(candidate.id);
    }
  }

  return groups.sort((a, b) => {
    const aMaxScore = Math.max(...a.duplicates.map(d => d.match.score));
    const bMaxScore = Math.max(...b.duplicates.map(d => d.match.score));
    return bMaxScore - aMaxScore;
  });
}

/**
 * Find potential duplicates in a list of companies
 */
export function findCompanyDuplicates(
  companies: Array<{
    id: string;
    name?: string | null;
    website_url?: string | null;
    location?: string | null;
  }>,
  minScore: number = THRESHOLDS.low
): Array<{
  primary: typeof companies[0];
  duplicates: Array<{ company: typeof companies[0]; match: MatchResult }>;
}> {
  const seen = new Set<string>();
  const groups: Array<{
    primary: typeof companies[0];
    duplicates: Array<{ company: typeof companies[0]; match: MatchResult }>;
  }> = [];

  for (let i = 0; i < companies.length; i++) {
    const company = companies[i];
    if (seen.has(company.id)) continue;

    const duplicates: Array<{ company: typeof companies[0]; match: MatchResult }> = [];

    for (let j = i + 1; j < companies.length; j++) {
      const other = companies[j];
      if (seen.has(other.id)) continue;

      const match = calculateCompanyMatch(company, other);
      if (match.score >= minScore) {
        duplicates.push({ company: other, match });
        seen.add(other.id);
      }
    }

    if (duplicates.length > 0) {
      groups.push({
        primary: company,
        duplicates: duplicates.sort((a, b) => b.match.score - a.match.score),
      });
      seen.add(company.id);
    }
  }

  return groups.sort((a, b) => {
    const aMaxScore = Math.max(...a.duplicates.map(d => d.match.score));
    const bMaxScore = Math.max(...b.duplicates.map(d => d.match.score));
    return bMaxScore - aMaxScore;
  });
}
